import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X } from 'lucide-react';
import { cn } from '@utils/cn';

export interface FileDropZoneProps {
  onDrop: (files: File[]) => void;
  accept?: Record<string, string[]>;
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  error?: string;
  compact?: boolean;
}

export const FileDropZone: React.FC<FileDropZoneProps> = ({
  onDrop,
  accept,
  maxSize,
  multiple = true,
  disabled = false,
  error,
  compact = false,
}) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      onDrop(acceptedFiles);
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone({
      onDrop: handleDrop,
      accept,
      maxSize,
      multiple,
      disabled,
    });

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={cn(
          'relative rounded-lg border-2 border-dashed transition-colors duration-200',
          'flex flex-col items-center justify-center cursor-pointer',
          compact ? 'gap-2 p-4' : 'gap-3 p-8',
          isDragActive && 'border-primary-500 bg-primary-50 dark:bg-primary-950',
          isDragAccept && 'border-green-500 bg-green-50 dark:bg-green-950',
          isDragReject && 'border-red-500 bg-red-50 dark:bg-red-950',
          !isDragActive && 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />
        <Upload className={cn('text-gray-400', compact ? 'h-6 w-6' : 'h-10 w-10')} />
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Drag files here or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {maxSize ? `Max file size: ${(maxSize / 1024 / 1024).toFixed(1)}MB` : 'Any file size'}
          </p>
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800">
          <X className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
